import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '../utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98] hover:scale-[1.02] transform-gpu",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md transition-all active:bg-primary/80 active:shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm border border-destructive/20 active:bg-destructive/80",
        outline:
          "border-2 border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent shadow-sm active:bg-accent/80 active:border-accent/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm border border-secondary/20 active:bg-secondary/70",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground transition-all active:bg-accent/70",
        link: "text-primary underline-offset-4 hover:underline active:opacity-80",
        hero: "bg-gradient-primary text-white hover:opacity-90 shadow-elegant font-semibold active:opacity-80 active:scale-[0.97]",
        cta: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow font-semibold px-6 py-3 active:bg-accent/80 active:shadow-md",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm border border-success/20 active:bg-success/80",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm border border-warning/20 active:bg-warning/80",
        info: "bg-info text-info-foreground hover:bg-info/90 shadow-sm border border-info/20 active:bg-info/80",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm font-medium border border-accent/20 active:bg-accent/80",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
